import React, { createContext, useContext, useState, useEffect, ReactNode, useRef, useCallback } from 'react';
import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { User, RegisterFormData } from '../types';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Chiavi per localStorage
const TOKEN_KEY = 'horecaplat_token';
const REFRESH_TOKEN_KEY = 'horecaplat_refresh_token';

// Funzione globale per applicare branding (sarà impostata da PrimaryColorContext)
let applyBrandingCallback: ((branding: any) => void) | null = null;
export const setApplyBrandingCallback = (callback: (branding: any) => void) => {
  applyBrandingCallback = callback;
};

interface AuthContextType {
  user: User | null;
  token: string | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<User>;
  register: (data: RegisterFormData) => Promise<User>;
  logout: () => Promise<void>;
  logoutAll: () => Promise<void>;
  updateUser: (updates: Partial<User>) => void;
  isAuthenticated: boolean;
  isBuyer: boolean;
  isSeller: boolean;
  isAdmin: boolean;
  hasFullBuyerFeatures: boolean;
  hasAssociationFeatures: boolean;
  isAssociatedOnly: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(() => localStorage.getItem(TOKEN_KEY));
  const [loading, setLoading] = useState(true);
  
  // Ref per gestire richieste di refresh in parallelo
  const isRefreshing = useRef(false);
  const refreshSubscribers = useRef<((token: string) => void)[]>([]);

  // Funzione per notificare le richieste in coda dopo il refresh
  const onRefreshed = useCallback((newToken: string) => {
    refreshSubscribers.current.forEach(callback => callback(newToken));
    refreshSubscribers.current = [];
  }, []);

  // Aggiungi una richiesta alla coda di attesa del refresh
  const addRefreshSubscriber = useCallback((callback: (token: string) => void) => {
    refreshSubscribers.current.push(callback);
  }, []);

  // Funzione per refreshare i token
  const refreshTokens = useCallback(async (): Promise<string | null> => {
    const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
    if (!refreshToken) {
      return null;
    }

    try {
      const response = await axios.post(`${API}/auth/refresh`, {
        refresh_token: refreshToken
      }, {
        // Skip interceptor per questa richiesta
        headers: { 'X-Skip-Interceptor': 'true' }
      });

      const { access_token, refresh_token: newRefreshToken } = response.data;
      
      // Salva i nuovi token
      localStorage.setItem(TOKEN_KEY, access_token);
      localStorage.setItem(REFRESH_TOKEN_KEY, newRefreshToken);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      setToken(access_token);
      
      return access_token;
    } catch (error) {
      // Refresh fallito, pulisci tutto
      clearTokens();
      return null;
    }
  }, []);

  // Pulisci tutti i token
  const clearTokens = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
  }, []);

  // Setup interceptor per gestire token scaduti
  useEffect(() => {
    const interceptorId = axios.interceptors.response.use(
      (response) => response,
      async (error: AxiosError) => {
        const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };
        
        // Skip se è una richiesta di refresh o se abbiamo già riprovato
        if (
          originalRequest?.headers?.['X-Skip-Interceptor'] ||
          originalRequest?._retry ||
          error.response?.status !== 401
        ) {
          return Promise.reject(error);
        }

        // Verifica se l'errore è per token scaduto
        const errorDetail = (error.response?.data as any)?.detail;
        if (errorDetail !== 'Token expired' && errorDetail !== 'Token scaduto') {
          return Promise.reject(error);
        }

        originalRequest._retry = true;

        // Se stiamo già facendo un refresh, metti la richiesta in coda
        if (isRefreshing.current) {
          return new Promise((resolve, reject) => {
            addRefreshSubscriber((newToken: string) => {
              if (originalRequest.headers) {
                originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
              }
              resolve(axios(originalRequest));
            });
          });
        }

        isRefreshing.current = true;

        try {
          const newToken = await refreshTokens();
          
          if (newToken) {
            // Notifica le richieste in coda
            onRefreshed(newToken);
            
            // Riprova la richiesta originale
            if (originalRequest.headers) {
              originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
            }
            return axios(originalRequest);
          } else {
            // Refresh fallito, redirect al login
            return Promise.reject(error);
          }
        } finally {
          isRefreshing.current = false;
        }
      }
    );

    return () => {
      axios.interceptors.response.eject(interceptorId);
    };
  }, [refreshTokens, onRefreshed, addRefreshSubscriber]);

  // Carica l'utente all'avvio
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, []);

  const fetchUser = async () => {
    try {
      const res = await axios.get(`${API}/auth/me`);
      setUser(res.data);
      
      // Applica branding associazione se presente
      if (res.data.association_branding && applyBrandingCallback) {
        applyBrandingCallback(res.data.association_branding);
      }
    } catch (error: any) {
      // Se il token è scaduto, prova a refreshare
      if (error.response?.status === 401) {
        const newToken = await refreshTokens();
        if (newToken) {
          // Riprova a caricare l'utente
          try {
            const res = await axios.get(`${API}/auth/me`);
            setUser(res.data);
            
            // Applica branding associazione se presente
            if (res.data.association_branding && applyBrandingCallback) {
              applyBrandingCallback(res.data.association_branding);
            }
            return;
          } catch {
            // Fallback: pulisci tutto
          }
        }
      }
      clearTokens();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email: string, password: string): Promise<User> => {
    // Modalità test: usa password vuota o "test" per login senza password
    const loginPassword = password || "test";
    const res = await axios.post(`${API}/auth/login`, { email, password: loginPassword });
    const { access_token, refresh_token, user: userData } = res.data;
    
    // Salva entrambi i token
    localStorage.setItem(TOKEN_KEY, access_token);
    localStorage.setItem(REFRESH_TOKEN_KEY, refresh_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    
    setToken(access_token);
    setUser(userData);
    
    // Dopo il login, carica il profilo completo per ottenere il branding E association_status
    try {
      const meRes = await axios.get(`${API}/auth/me`);
      // Aggiorna user con i dati completi incluso association_status
      setUser(meRes.data);
      if (meRes.data.association_branding && applyBrandingCallback) {
        applyBrandingCallback(meRes.data.association_branding);
      }
    } catch {
      // Ignora errori
    }
    
    return userData;
  };

  const register = async (data: RegisterFormData): Promise<User> => {
    const res = await axios.post(`${API}/auth/register`, data);
    const { access_token, user: userData } = res.data;
    
    // La registrazione potrebbe non restituire refresh token (usa token singolo)
    localStorage.setItem(TOKEN_KEY, access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const logout = async () => {
    const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
    
    // Prova a invalidare il token sul server
    if (refreshToken) {
      try {
        await axios.post(`${API}/auth/logout`, { refresh_token: refreshToken });
      } catch (error) {
        // Ignora errori, pulisci comunque i token locali
        console.warn('Logout server error:', error);
      }
    }
    
    clearTokens();
  };

  const logoutAll = async () => {
    try {
      await axios.post(`${API}/auth/logout-all`);
    } catch (error) {
      console.warn('Logout all sessions error:', error);
    }
    clearTokens();
  };

  const updateUser = (updates: Partial<User>) => {
    setUser(prev => prev ? { ...prev, ...updates } : null);
  };

  // Calcola i flag per le features in base all'association_status
  const associationStatus = user?.association_status as {
    type?: string;
    has_full_buyer_features?: boolean;
    has_association_features?: boolean;
  } | undefined;
  
  const hasFullBuyerFeatures = associationStatus?.has_full_buyer_features ?? true;
  const hasAssociationFeatures = associationStatus?.has_association_features ?? false;
  const isAssociatedOnly = associationStatus?.type === 'associated_only';

  return (
    <AuthContext.Provider value={{ 
      user, 
      token, 
      loading, 
      login, 
      register, 
      logout,
      logoutAll,
      updateUser,
      isAuthenticated: !!user,
      isBuyer: user?.role === 'buyer',
      isSeller: user?.role === 'seller',
      isAdmin: user?.role === 'admin',
      hasFullBuyerFeatures,
      hasAssociationFeatures,
      isAssociatedOnly
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
